/**
 * 通信バックエンドの実行時切替（自前・GPL非依存）。
 *
 * 環境変数 `VITE_PARTYKIT_HOST`（ビルド時に埋め込み）が設定されていれば PartyKit
 * （遠隔・WebSocket）を、未設定なら従来どおりループバック（同一ブラウザの複数タブ）を使う。
 * これにより、デプロイ前後でアプリ側コードを変えずに対戦経路を切り替えられる。
 *
 * 開発用バイパス: `VITE_LOCAL_LOBBY=1`（または `true`）のときは、`VITE_PARTYKIT_HOST`
 * が設定されていても**強制的にループバック（オフライン・同一ブラウザ）**を使う。中継
 * サーバー（`npm run party:dev` / 本番リレー）に繋がらない環境でも、ニックネーム確定で
 * 止まらずローカル単体の動作確認を行うための回避経路。`.env.local` は Git 管理外のため
 * 本番ビルドには載らず、本番の fail-closed（サーバー認証必須）は維持される。
 *
 * 設定方法は knowledge/operations/partykit-deploy-runbook.md を参照。
 */

import type { Transport } from './transport.js';
import type { LobbyClient } from './lobby.js';
import { createLoopbackTransport, createTransportWith } from './loopback.js';
import { createLoopbackLobby, createLobbyWith } from './loopback-lobby.js';
import { wsChannelFactory } from './ws-channel.js';

const HOST = import.meta.env.VITE_PARTYKIT_HOST;

/** 開発用バイパス: 中継を使わずループバックを強制するか（VITE_LOCAL_LOBBY）。 */
const LOCAL_LOBBY =
  import.meta.env.VITE_LOCAL_LOBBY === '1' ||
  import.meta.env.VITE_LOCAL_LOBBY === 'true';

/** 遠隔中継（PartyKit）を使う構成か（HOST 設定済み かつ バイパス無効）。 */
export const isRemoteNet = (): boolean =>
  typeof HOST === 'string' && HOST.length > 0 && !LOCAL_LOBBY;

/** 対戦トランスポートを生成する（構成に応じて PartyKit / ループバック）。 */
export const createTransport = (room: string): Transport => {
  if (HOST && !LOCAL_LOBBY) return createTransportWith(wsChannelFactory(HOST), room);
  return createLoopbackTransport(room);
};

/** ロビーを生成する（構成に応じて PartyKit / ループバック）。
 *  PartyKit 時のみ serverBacked=true（名前重複防止・成績の端末間共有が有効）。
 *  VITE_LOCAL_LOBBY=1 ならバイパスしてループバック（serverBacked=false＝認証は 'local'）。 */
export const createLobby = (): LobbyClient => {
  if (HOST && !LOCAL_LOBBY) return createLobbyWith(wsChannelFactory(HOST), true);
  return createLoopbackLobby();
};
